import { enqueueSnackbar } from "notistack";
import {
  createContext,
  ReactElement,
  ReactNode,
  useMemo,
  useState,
} from "react";

import {
  AssetMetashotsType,
  AssetStatus,
  AssetType,
  AssetTypes,
  Category,
  Collection,
  LanguageRegion,
} from "../generated/gql/graphql";
import useCreateAsset from "../hooks/mutation/use-create-asset";
import useText from "../hooks/use-text";
import { LocalizedStrings, SupportedLanguages } from "../utils/lang";
import { emptyFn } from "../utils/provider";

export enum UploadFormErrorTypes {
  MISSING_ASSET_TYPE = "missing_asset_type",
  MISSING_STATUS_TYPE = "missing_status",
}

/** Definition of the set of variables to be uploaded */
type Options = {
  assetStatus?: AssetStatus;
  setAssetStatus(assetStatus?: AssetStatus): void;

  assetTypes?: AssetTypes;
  setAssetTypes(assetTypes?: AssetTypes): void;

  metashotType?: AssetMetashotsType;
  setMetashotType(metashotTypes?: AssetMetashotsType): void;

  languageRegion?: LanguageRegion;
  setLanguageRegion(languageRegion?: LanguageRegion): void;

  projectId?: string;
  setProjectId(projectId?: string): void;

  masterId?: string;
  setMasterId(masterId?: string): void;

  adaptionId?: string;
  setAdaptionId(adaptionId?: string): void;

  collection?: Collection;
  setCollection(collection?: Collection): void;

  category?: Category;
  setCategory(category?: Category): void;

  productTypeId?: string;
  setProductTypeId(productTypeId: string): void;

  labelTypeId?: string;
  setLabelTypeId(labelTypeId: string): void;

  typeOfShotId?: string;
  setTypeOfShotId(typeOfShotId?: string): void;

  files: File[];
  setFiles(files: File[]): void;

  isCreateAssetLoading: boolean;
};

type UploadProviderContextType = Options & {
  errors: UploadFormErrorTypes[];
  upload(): void;
  clear(): void;
};

export const UploadContext = createContext<UploadProviderContextType>({
  errors: [],
  isCreateAssetLoading: false,
  files: [],
  setFiles: emptyFn,
  upload: emptyFn,
  setAssetTypes: emptyFn,
  setAssetStatus: emptyFn,
  setMetashotType: emptyFn,
  setLanguageRegion: emptyFn,
  setCollection: emptyFn,
  setProjectId: emptyFn,
  setMasterId: emptyFn,
  setAdaptionId: emptyFn,
  setCategory: emptyFn,
  setProductTypeId: emptyFn,
  setLabelTypeId: emptyFn,
  setTypeOfShotId: emptyFn,
  clear: emptyFn,
});

type Props = {
  children: ReactNode;
};

export default function UploadProvider(props: Props): ReactElement {
  const { children } = props;

  const { strings } = useText(local);

  const [files, setFiles] = useState<File[]>([]);
  const [assetTypes, setAssetTypes] = useState<AssetTypes>();
  const [assetStatus, setAssetStatus] = useState<AssetStatus>();
  const [metashotType, setMetashotType] = useState<AssetMetashotsType>();
  const [languageRegion, setLanguageRegion] = useState<LanguageRegion>();
  const [projectId, setProjectId] = useState<string>();
  const [masterId, setMasterId] = useState<string>();
  const [adaptionId, setAdaptionId] = useState<string>();
  const [collection, setCollection] = useState<Collection>();
  const [category, setCategory] = useState<Category>();
  const [productTypeId, setProductTypeId] = useState<string>();
  const [labelTypeId, setLabelTypeId] = useState<string>();
  const [typeOfShotId, setTypeOfShotId] = useState<string>();
  const [errors, setErrors] = useState<UploadFormErrorTypes[]>([]);

  const { createAsset, isCreateAssetLoading } = useCreateAsset();

  /** Create and upload file as assets. */
  async function upload(): Promise<void> {
    if (files.length === 0) {
      return;
    }

    // ToDo: add role !== Roles.regular guard

    if (!assetTypes || !assetStatus) {
      const uploadErrors = [];

      if (!assetTypes) {
        uploadErrors.push(UploadFormErrorTypes.MISSING_ASSET_TYPE);
      }

      if (!assetStatus) {
        uploadErrors.push(UploadFormErrorTypes.MISSING_STATUS_TYPE);
      }

      setErrors(uploadErrors);
      return;
    }

    try {
      const calls = files.map(async (file) => {
        const params = {
          type: AssetType.Image,
          name: file.name,
          status: assetStatus || AssetStatus.Draft,
          asset_types: assetTypes,
          image: file,
          metashots_type: metashotType,
          language_regions_id: languageRegion ? languageRegion.id : undefined,
          collection_id: collection ? [collection.id] : undefined,
          category_id: category?.id,
          projectId,
          masterId,
          adaptionId,
          productTypeId: productTypeId ? productTypeId : undefined,
          labelTypeId: labelTypeId ? labelTypeId : undefined,
          typeOfShotId: typeOfShotId ? typeOfShotId : undefined,
        };

        await createAsset(params);
      });

      // ToDo: Create metaphore_approval_session

      await Promise.all(calls);
      enqueueSnackbar(strings.info.success, { variant: "success" });
      clear();
    } catch (err) {
      enqueueSnackbar(strings.info.failed, { variant: "error" });
    }
  }

  /** Clear all values that a selected. */
  function clear(): void {
    // Options
    setFiles([]);
    setAssetTypes(undefined);
    setAssetStatus(undefined);
    setMetashotType(undefined);
    setLanguageRegion(undefined);
    setCollection(undefined);
    setCategory(undefined);
    setProjectId(undefined);
    setMasterId(undefined);
    setAdaptionId(undefined);
    setProductTypeId(undefined);
    setLabelTypeId(undefined);
    setTypeOfShotId(undefined);

    // General
    setErrors([]);
  }

  const values = useMemo(
    () => ({
      // Options
      files,
      setFiles,
      assetTypes,
      setAssetTypes,
      assetStatus,
      setAssetStatus,
      metashotType,
      setMetashotType,
      languageRegion,
      setLanguageRegion,
      projectId,
      setProjectId,
      masterId,
      setMasterId,
      adaptionId,
      setAdaptionId,
      collection,
      setCollection,
      category,
      setCategory,
      productTypeId,
      setProductTypeId,
      labelTypeId,
      setLabelTypeId,
      typeOfShotId,
      setTypeOfShotId,

      // General
      isCreateAssetLoading,
      errors,
      upload,
      clear,
    }),
    [
      files,
      isCreateAssetLoading,
      assetStatus,
      projectId,
      masterId,
      adaptionId,
      assetTypes,
      metashotType,
      languageRegion,
      collection,
      category,
      productTypeId,
      labelTypeId,
      typeOfShotId,
      errors,
    ]
  );

  return (
    <UploadContext.Provider value={values}>{children}</UploadContext.Provider>
  );
}

type LocalSchema = {
  info: {
    success: string;
    failed: string;
  };
};

const local: LocalizedStrings<LocalSchema, SupportedLanguages> = {
  default: {
    info: {
      success: "Files was uploaded.",
      failed: "Failed upload.",
    },
  },
};
